/**
 * System settings page
 * Configure upload limits, Haravan integration, and view usage statistics
 */
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import SettingsForm from '@/components/admin/SettingsForm';
import { Save, Loader2 } from 'lucide-react';

export default function SystemSettings() {
  const { adminUser } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<any>(null);
  const [usageStats, setUsageStats] = useState<any>(null);

  useEffect(() => {
    loadSettings();
    loadUsageStats();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // Load upload limits
      const uploadLimitsDoc = await getDoc(doc(db, 'system_settings', 'upload_limits'));
      const uploadLimits = uploadLimitsDoc.exists() ? uploadLimitsDoc.data() : null;

      // Load Haravan settings
      const haravanDoc = await getDoc(doc(db, 'system_settings', 'haravan_integration'));
      const haravan = haravanDoc.exists() ? haravanDoc.data() : null;

      setSettings({
        upload_limits: uploadLimits?.setting_value || {
          per_ip_hourly: 10,
          per_ip_daily: 50,
          per_ip_monthly: 500,
          per_session: 20,
          global_daily: 1000
        },
        haravan_integration: haravan?.setting_value || {
          enabled: false,
          store_url: '',
          phone_case_product_path: '',
          coffee_cup_product_path: '',
          widget_button_text: 'Thiết Kế Ngay',
          widget_button_color: '#FF6B6B',
          redirect_url_template: ''
        }
      });
    } catch (error: any) {
      console.error('Error loading settings:', error);
      // Don't block UI with alert - just log error
    } finally {
      setLoading(false);
    }
  };

  const loadUsageStats = async () => {
    try {
      // Get today's date range
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // Count submissions today
      const todayQuery = query(
        collection(db, 'design_submissions'),
        where('created_at', '>=', today),
        where('created_at', '<', tomorrow)
      );
      const todaySnapshot = await getDocs(todayQuery);
      const todayCount = todaySnapshot.size;

      // Count submissions this week
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekQuery = query(
        collection(db, 'design_submissions'),
        where('created_at', '>=', weekAgo)
      );
      const weekSnapshot = await getDocs(weekQuery);
      const weekCount = weekSnapshot.size;

      // Count submissions this month
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      const monthQuery = query(
        collection(db, 'design_submissions'),
        where('created_at', '>=', monthAgo)
      );
      const monthSnapshot = await getDocs(monthQuery);
      const monthCount = monthSnapshot.size;

      setUsageStats({
        today: todayCount,
        thisWeek: weekCount,
        thisMonth: monthCount
      });
    } catch (error: any) {
      console.error('Error loading usage stats:', error);
    }
  };

  const handleSave = async (updatedSettings: any) => {
    if (!adminUser) return;

    try {
      setSaving(true);

      // Save upload limits
      await setDoc(
        doc(db, 'system_settings', 'upload_limits'),
        {
          setting_key: 'upload_limits',
          setting_value: updatedSettings.upload_limits,
          updated_at: new Date(),
          updated_by: adminUser.user.uid
        }
      );

      // Save Haravan integration
      await setDoc(
        doc(db, 'system_settings', 'haravan_integration'),
        {
          setting_key: 'haravan_integration',
          setting_value: updatedSettings.haravan_integration,
          updated_at: new Date(),
          updated_by: adminUser.user.uid
        }
      );

      setSettings(updatedSettings);
      // Success feedback without blocking UI
      console.log('Settings saved successfully');
    } catch (error: any) {
      console.error('Error saving settings:', error);
      // Don't block UI with alert - just log error
      throw error; // Re-throw so caller can handle if needed
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Cài đặt hệ thống</h1>
        <p className="text-gray-600">
          Cấu hình giới hạn tải lên, tích hợp Haravan và xem thống kê sử dụng
        </p>
      </div>

      {/* Usage Statistics */}
      {usageStats && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Thống kê sử dụng</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 font-medium mb-1">Hôm nay</div>
              <div className="text-2xl font-bold text-blue-900">{usageStats.today}</div>
              <div className="text-xs text-blue-600 mt-1">bản gửi</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 font-medium mb-1">Tuần này</div>
              <div className="text-2xl font-bold text-green-900">{usageStats.thisWeek}</div>
              <div className="text-xs text-green-600 mt-1">bản gửi</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-purple-600 font-medium mb-1">Tháng này</div>
              <div className="text-2xl font-bold text-purple-900">{usageStats.thisMonth}</div>
              <div className="text-xs text-purple-600 mt-1">bản gửi</div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      {settings && (
        <SettingsForm
          settings={settings}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}

